import { Box, Button, useTheme } from '@mui/material';
import { ResponsiveStyleValue } from '@mui/system';
import { useEffect, useMemo, useCallback, type JSX } from 'react';
import { PaymentInfo } from '@repo/types';
import { useLocale } from '@repo/i18n';
import { showError } from 'src/utils/widget/error-handling';
import { capitalize } from '@repo/common-utils/TextUtils';
import AdyenPayment from './AdyenPayment';

interface AdyenPaymentWrapperProps {
    paymentInfo: PaymentInfo;
    onPaymentCancelled: () => void;
    onPaymentCompleted: (paymentInfo: PaymentInfo, paymentGatewayResponse: any) => void;
}

const FOURTEEN_MINUTES_THIRTY_SECONDS = 1000 * 60 * 14.5;

export default function AdyenPaymentWrapper(props: AdyenPaymentWrapperProps): JSX.Element {
    const { t } = useLocale();
    const timeOfMount = useMemo(() => Date.now(), []);
    const theme = useTheme();

    const goBackClicked = useCallback(() => {
        props.onPaymentCancelled();
    }, [props]);

    const handlePaymentFailed = useCallback(
        (result: unknown) => {
            const message =
                result && typeof result === 'object' && 'resultCode' in result
                    ? String(result.resultCode)
                    : t.anErrorOccurredDuringPayment;
            showError(capitalize(message));
        },
        [t.anErrorOccurredDuringPayment],
    );

    useEffect(() => {
        const interval = setInterval(() => {
            if (Date.now() - timeOfMount > FOURTEEN_MINUTES_THIRTY_SECONDS) {
                alert(t.your_reservation_timed_out);
                goBackClicked();
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [timeOfMount, goBackClicked, t.your_reservation_timed_out]);

    return (
        <Box
            color={theme.palette.text.primary}
            fontFamily={theme.typography.fontFamily as ResponsiveStyleValue<any>}
            fontSize={theme.typography.fontSize}
            pt={0}
            minWidth="300px"
            width="100%"
            sx={{
                '& > *': {
                    marginTop: theme.spacing(1),
                    marginBottom: theme.spacing(1),
                },

                [theme.breakpoints.down('md')]: {
                    marginBottom: theme.spacing(6),
                    maxWidth: '100%',
                },
            }}
        >
            <AdyenPayment
                context={props.paymentInfo}
                onPaymentCompleted={props.onPaymentCompleted}
                onPaymentFailed={handlePaymentFailed}
            />
            <Button variant="outlined" color="primary" onClick={goBackClicked}>
                {t.go_back}
            </Button>
        </Box>
    );
}
